import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RelayLoader } from "../components/RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;
const INK = "#2A1E5C";
const GOLD = "#F5CB5C";

const RETRY_PATH = {
  momentum_map: "/start/free-sprint",
  full_payday_relay: "/start/season-pass",
  pit_crew_bundle: "/start/season-pass",
  free_sprint: "/start/free-sprint",
  season_monthly: "/start/season-pass",
  season_annual: "/start/season-pass?plan=annual",
};

export default function CheckoutReturnPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("payment") === "cancelled";
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId || cancelled) { if (!cancelled) setState("error"); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/checkout/return-status?session_id=${encodeURIComponent(sessionId)}`, { credentials: "include" });
        const data = await res.json();
        if (!res.ok) throw new Error();
        setInfo(data);
        if (data.status === "complete") {
          setState("complete");
          setTimeout(() => { window.location.href = data.next_path || "/"; }, 2500);
        } else if (data.status === "open") {
          setState("open");
        } else {
          setState("error");
        }
      } catch {
        setState("error");
      }
    })();
  }, [sessionId, cancelled]);

  const retryPath = RETRY_PATH[info?.product_id] || "/start/free-sprint";

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: INK }}>
      <div className="w-full max-w-md rounded-2xl p-8 text-center shadow-2xl" style={{ background: "#FDFBF7" }} data-testid="checkout-return-card">
        {state === "checking" && (
          <div data-testid="checkout-checking">
            <RelayLoader size="md" label="Checking your payment…" />
          </div>
        )}
        {state === "complete" && (
          <div data-testid="checkout-success">
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>You're in!</h1>
            <p className="text-sm mb-6" style={{ color: "#5D5A6B" }}>
              {info?.signed_in ? "You're signed in" : "Payment confirmed"}{info?.customer_email ? ` as ${info.customer_email}` : ""}. A receipt is on its way.
              Step 1: calculate your relay numbers. Step 2: sync your Google Sheet. Step 3: start logging. Taking you to Step 1…
            </p>
            <a
              href={info?.next_path || "/"}
              className="inline-block px-8 py-3.5 rounded-full font-bold text-base"
              style={{ background: GOLD, color: INK }}
              data-testid="checkout-continue-btn"
            >
              Go to Step 1 →
            </a>
          </div>
        )}
        {state === "open" && (
          <div data-testid="checkout-open">
            <h1 className="text-xl font-bold mb-2" style={{ color: INK }}>Payment not finished yet</h1>
            <p className="text-sm mb-6" style={{ color: "#5D5A6B" }}>Your checkout is still open. You can pick up right where you left off.</p>
            <Link to={retryPath} className="inline-block px-8 py-3.5 rounded-full font-bold" style={{ background: GOLD, color: INK }} data-testid="checkout-retry-btn">
              Try again →
            </Link>
          </div>
        )}
        {state === "cancelled" && (
          <div data-testid="checkout-cancelled">
            <h1 className="text-xl font-bold mb-2" style={{ color: INK }}>No charge made</h1>
            <p className="text-sm mb-6" style={{ color: "#5D5A6B" }}>You backed out before paying. Nothing was charged. Whenever you're ready, your relay is waiting.</p>
            <Link to="/" className="inline-block px-8 py-3.5 rounded-full font-bold" style={{ background: GOLD, color: INK }}>Back to paydayrelay.com</Link>
          </div>
        )}
        {state === "error" && (
          <div data-testid="checkout-error">
            <h1 className="text-xl font-bold mb-2" style={{ color: INK }}>We couldn't confirm that payment</h1>
            <p className="text-sm mb-6" style={{ color: "#5D5A6B" }}>
              If you were charged, your access is still being granted by email. Otherwise, give it another go, 
              or write to support@paydayrelay.com and we'll sort it fast.
            </p>
            <Link to={retryPath} className="inline-block px-8 py-3.5 rounded-full font-bold" style={{ background: GOLD, color: INK }}>Try again →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
